(() => {
class O2MediaCarousel {
  constructor(root) {
    this.root = root;
    this.track = root.querySelector('[data-o2-carousel-track]');
    this.thumbnailTrack = root.querySelector('[data-o2-carousel-thumbnails]');
    this.currentElement = root.querySelector('[data-o2-carousel-current]');
    this.totalElement = root.querySelector('[data-o2-carousel-total]');
    this.statusElement =
      root.querySelector('[data-o2-carousel-status]') || root.querySelector('[id^="GalleryStatus-"]');
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    if (!this.track) return;

    this.onClick = this.onClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.root.addEventListener('click', this.onClick);
    this.root.addEventListener('keydown', this.onKeydown);
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.resizeObserver = new ResizeObserver(() => {
      if (this.activeMediaId) this.scrollToMedia(this.activeMediaId, false, false);
    });
    this.resizeObserver.observe(this.track);
    this.refresh();
  }

  get items() {
    return Array.from(this.track?.children || []).filter((item) => !item.hidden && item.dataset.mediaId);
  }

  get thumbnails() {
    return Array.from(this.thumbnailTrack?.querySelectorAll('[data-target]') || []);
  }

  destroy() {
    if (!this.track) return;
    window.clearTimeout(this.scrollTimer);
    this.root.removeEventListener('click', this.onClick);
    this.root.removeEventListener('keydown', this.onKeydown);
    this.track.removeEventListener('scroll', this.onScroll);
    this.resizeObserver.disconnect();
  }

  refresh(preferredMediaId) {
    if (!this.track) return;
    if (this.root.hasAttribute('data-sync-thumbnails')) this.syncThumbnails();

    const items = this.items;
    if (!items.length) return;

    const activeItem =
      items.find((item) => item.dataset.mediaId === preferredMediaId) ||
      items.find((item) => item.classList.contains('is-active')) ||
      items[0];
    this.updateActive(items.indexOf(activeItem), false);
  }

  syncThumbnails() {
    const mediaIds = new Set(this.items.map((item) => item.dataset.mediaId));
    this.thumbnails.forEach((thumbnail) => {
      thumbnail.toggleAttribute('hidden', !mediaIds.has(thumbnail.dataset.target));
    });
  }

  setActiveMedia(mediaId, prepend = false) {
    if (!this.track) return;

    const item = this.items.find((candidate) => candidate.dataset.mediaId === mediaId);
    const thumbnail = this.thumbnails.find((candidate) => candidate.dataset.target === mediaId);

    if (prepend && item) {
      this.track.prepend(item);
      if (thumbnail) this.thumbnailTrack.prepend(thumbnail);
    }

    this.refresh(mediaId);
    this.scrollToMedia(mediaId, false);
  }

  onClick(event) {
    const directionButton = event.target.closest('[data-o2-carousel-direction]');
    if (directionButton && this.root.contains(directionButton)) {
      event.preventDefault();
      this.navigate(Number(directionButton.dataset.o2CarouselDirection), true);
      return;
    }

    const thumbnail = event.target.closest('[data-target]');
    if (!thumbnail || !this.root.contains(thumbnail)) return;

    event.preventDefault();
    this.scrollToMedia(thumbnail.dataset.target, true);
  }

  onKeydown(event) {
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    if (event.target.closest('input, select, textarea, video, model-viewer, iframe')) return;

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.navigate(-1, false);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.navigate(1, false);
    }
  }

  onScroll() {
    window.clearTimeout(this.scrollTimer);
    this.scrollTimer = window.setTimeout(() => {
      const index = this.closestIndex();
      if (index !== -1) this.updateActive(index, true);
    }, 80);
  }

  closestIndex() {
    const items = this.items;
    if (!items.length) return -1;

    const scrollPosition = this.track.scrollLeft + this.track.offsetLeft;
    return items.reduce((closestIndex, item, index) => {
      const closestDistance = Math.abs(items[closestIndex].offsetLeft - scrollPosition);
      const itemDistance = Math.abs(item.offsetLeft - scrollPosition);
      return itemDistance < closestDistance ? index : closestIndex;
    }, 0);
  }

  navigate(direction, animate) {
    const items = this.items;
    if (items.length < 2) return;

    const currentIndex = Math.max(0, items.findIndex((item) => item.dataset.mediaId === this.activeMediaId));
    const nextIndex = (currentIndex + direction + items.length) % items.length;
    const wraps = Math.abs(nextIndex - currentIndex) > 1;

    // ponytail: wrap jumps instead of cloning slides; clone only if seamless looping becomes a requirement.
    this.scrollToIndex(nextIndex, animate && !wraps);
  }

  scrollToMedia(mediaId, animate, announce = true) {
    const index = this.items.findIndex((item) => item.dataset.mediaId === mediaId);
    if (index === -1) return;
    this.scrollToIndex(index, animate, announce);
  }

  scrollToIndex(index, animate, announce = true) {
    const item = this.items[index];
    if (!item) return;

    const shouldAnimate = animate && !this.reducedMotion.matches;
    // offsetLeft is relative to the shared offsetParent, not to the scroll container.
    this.track.scrollTo({
      left: item.offsetLeft - this.track.offsetLeft,
      behavior: shouldAnimate ? 'smooth' : 'instant',
    });

    this.updateActive(index, announce, shouldAnimate);
  }

  updateActive(index, announce, animateThumbnail = false) {
    const items = this.items;
    const activeItem = items[index];
    if (!activeItem) return;

    const previousMediaId = this.activeMediaId;
    this.activeMediaId = activeItem.dataset.mediaId;
    this.activeIndex = index;

    items.forEach((item, itemIndex) => {
      const isActive = itemIndex === index;
      item.classList.toggle('is-active', isActive);
      item.setAttribute('aria-hidden', String(!isActive));
      item.toggleAttribute('inert', !isActive);
    });

    if (this.currentElement) this.currentElement.textContent = String(index + 1);
    if (this.totalElement) this.totalElement.textContent = String(items.length);

    const activeThumbnail = this.thumbnails.find((thumbnail) => thumbnail.dataset.target === this.activeMediaId);
    this.thumbnails.forEach((thumbnail) => {
      const link = thumbnail.querySelector('a, button');
      if (!link) return;
      if (thumbnail === activeThumbnail) {
        link.setAttribute('aria-current', 'true');
      } else {
        link.removeAttribute('aria-current');
      }
    });

    if (activeThumbnail) this.revealThumbnail(activeThumbnail, animateThumbnail);

    if (previousMediaId && previousMediaId !== this.activeMediaId) {
      window.pauseAllMedia();
      this.playActiveMedia(activeItem);
    }
    if (announce && this.statusElement && previousMediaId !== this.activeMediaId) {
      this.statusElement.textContent = (window.accessibilityStrings?.imageAvailable || '').replace(
        '[index]',
        index + 1
      );
    }
  }

  playActiveMedia(activeItem) {
    const deferredMedia = activeItem.querySelector('.deferred-media');
    if (deferredMedia) deferredMedia.loadContent(false);
  }

  revealThumbnail(thumbnail, animate) {
    if (!this.thumbnailTrack) return;

    const track = this.thumbnailTrack;
    const offset = thumbnail.offsetLeft - track.offsetLeft;
    track.scrollTo({
      left: offset - (track.clientWidth - thumbnail.offsetWidth) / 2,
      behavior: animate && !this.reducedMotion.matches ? 'smooth' : 'instant',
    });
  }
}

if (!customElements.get('media-gallery-o2')) {
  customElements.define(
    'media-gallery-o2',
    class MediaGalleryO2 extends HTMLElement {
      connectedCallback() {
        this.carousel = new O2MediaCarousel(this);
      }

      disconnectedCallback() {
        this.carousel?.destroy();
      }

      setActiveMedia(mediaId, prepend = false) {
        this.carousel?.setActiveMedia(mediaId, prepend);
      }
    }
  );
}

if (!customElements.get('product-modal-o2')) {
  customElements.define(
    'product-modal-o2',
    class ProductModalO2 extends ModalDialog {
      show(opener) {
        super.show(opener);
        this.carousel?.destroy();
        this.carousel = new O2MediaCarousel(this);
        this.carousel.setActiveMedia(opener.dataset.mediaId);
      }

      hide() {
        const mediaId = this.carousel?.activeMediaId;
        if (mediaId) {
          const gallery = document.querySelector(this.dataset.mainGallery);
          gallery?.setActiveMedia(`${this.dataset.section}-${mediaId}`);
          // The opener's slide is inert once another medium is active, so focus its live counterpart.
          this.openedBy = gallery?.querySelector('.is-active .product__media-toggle') || this.openedBy;
        }
        this.carousel?.destroy();
        this.carousel = null;
        super.hide();
      }
    }
  );
}
})();
